import { Component, OnInit } from "@angular/core";
import { ConfigCertUsedResiduals, ConfigNewResiduals, NewResiduals, ResidualValue } from "../../models/lease-residuals";
import { SettingsService } from "../../services";
import { AlertService } from "../../../shared-module/services";
import { FeatureFlag, FeatureFlagsService } from "src/app/clearpath-module/services/feature-flags.service";

@Component({
  selector: "app-lease-residuals",
  templateUrl: "./lease-residuals.component.html",
  styleUrls: ["./lease-residuals.component.scss"]
})
export class LeaseResidualsComponent implements OnInit {

  private uiState = {
    activeViewId: "viewNewResiduals",
    waitOn: {init: true, action: false},
    unsavedChanges: false,
    viewList: [
      {id: "viewNewResiduals", title: "View New Residuals"},
      {id: "viewCertUsedResiduals", title: "View Certified Residuals"},
      {id: "uploadNewResiduals", title: "Upload New Residuals"},
      {id: "uploadCertUsedResiduals", title: "Upload Certified Residuals"},
    ]
  };

  configNewResiduals: ConfigNewResiduals;
  configCertUsedResiduals: ConfigCertUsedResiduals;

  newResiduals: NewResiduals[] = [];
  certUsedResiduals: ResidualValue[] = [];

  toyotaResidualsImporterFlag: boolean = false;

  constructor(
    private settingsService: SettingsService,
    private alertService: AlertService,
    private featureFlagsService: FeatureFlagsService,
  ) { }

  ngOnInit() {
    this.getNewResiduals();
    this.getCertUsedResiduals();

    this.toyotaResidualsImporterFlag = this.featureFlagsService.getFeatureFlag(FeatureFlag.ToyotaResidualsImporter);
    this.checkResidualsButton();
    this.featureFlagsService.onFlagUpdated(FeatureFlag.ToyotaResidualsImporter, (value) => {
      this.toyotaResidualsImporterFlag = value;
      this.checkResidualsButton();
    });
  }

  canDeactivate(): boolean {
    const {unsavedChanges} = this.uiState;
    const navigateAway = unsavedChanges ? confirm("Discard Changes?") : true;
    return navigateAway;
  }

  // INITIALIZATION

  private getNewResiduals() {
    this.settingsService.getNewResiduals().subscribe({
      next: (data) => {
        this.configNewResiduals = data;
        this.newResiduals = data.newResiduals;
        this.uiState.waitOn.init = false;
      },
      error: (err) => {
        this.uiState.waitOn.init = false;
        this.alertService.error(err);
      }
    });
  }

  private getCertUsedResiduals() {
    this.settingsService.getCertUsedResiduals().subscribe({
      next: (data) => {
        this.configCertUsedResiduals = data;
        this.certUsedResiduals = data.residualValues;
      },
      error: (err) => {
        this.uiState.waitOn.init = false;
        this.alertService.error(err);
      }
    });
  }

  private setNewResiduals(newResiduals: NewResiduals[]) {
    this.uiState.waitOn.action = true;

    this.configNewResiduals.updatedAt = new Date()
    this.configNewResiduals.newResiduals = newResiduals

    this.settingsService.setNewResiduals(this.configNewResiduals).subscribe({
      next: (data: any) => {
        this.configNewResiduals = data;
        this.newResiduals = data.newResiduals;
        this.uiState.waitOn.action = false;
        this.uiState.unsavedChanges = false;
        this.alertService.success("New residuals successfully saved.");
        window.scroll({top: 0, behavior: "smooth"});
      },
      error: (err) => {
        this.uiState.waitOn.action = false;
        this.alertService.error(err);
        window.scroll({top: 0, behavior: "smooth"});
      }
    });
  }

  private setCertUsedResiduals(certUsedResiduals: ResidualValue[]) {
    this.uiState.waitOn.action = true;

    this.configCertUsedResiduals.updatedAt = new Date()
    this.configCertUsedResiduals.residualValues = certUsedResiduals

    this.settingsService.setCertUsedResiduals(this.configCertUsedResiduals).subscribe({
      next: (data: any) => {
        this.configCertUsedResiduals = data;
        this.certUsedResiduals = data.residualValues;
        this.uiState.waitOn.action = false;
        this.uiState.unsavedChanges = false;
        this.alertService.success("Certified used residuals successfully saved.");
        window.scroll({top: 0, behavior: "smooth"});
      },
      error: (err) => {
        this.uiState.waitOn.action = false;
        this.alertService.error(err);
        window.scroll({top: 0, behavior: "smooth"});
      }
    });
  }

  // ACTIONS

  onSelectView(id: string) {
    if (id === this.uiState.activeViewId) { return; }

    const {unsavedChanges} = this.uiState;
    const switchView = unsavedChanges ? confirm("Discard Changes?") : true;

    if (switchView) {
      this.uiState.activeViewId = id;
      this.uiState.unsavedChanges = false;
      this.alertService.clear();
    }
  }

  onSubmitNewResiduals(residuals: NewResiduals[]) {
    this.setNewResiduals(residuals);
  }

  onSubmitCertUsedResiduals(residuals: ResidualValue[]) {
    this.setCertUsedResiduals(residuals);
  }

  // UI STATE & RENDERING

  activeView(id: string): boolean {
    return id === this.uiState.activeViewId;
  }

  get viewList(): Array<{ id: string; title: string; }> {
    return this.uiState.viewList;
  }

  get waitOnAction(): boolean {
    return this.uiState.waitOn.action;
  }

  get waitOnInit(): boolean {
    return this.uiState.waitOn.init;
  }

  sortResidualsByYear(newResiduals: NewResiduals[]) {
    return newResiduals.sort((a, b) => {
      if (a.year < b.year) { return 1; }
      if (a.year > b.year) { return -1; }
      return 0;
    });
  }

  // HELPERS

  unsavedChanges(unsavedChanges: boolean) {
    this.uiState.unsavedChanges = unsavedChanges;
    this.alertService.clear();
  }

  checkResidualsButton() {
    if (this.toyotaResidualsImporterFlag) {
      if (this.uiState.viewList.findIndex((view) => view.id === "residualImporter") === -1) {
        this.uiState.viewList.push({id: "residualImporter", title: "Toyota Residual Importer"});
      }
      this.uiState.viewList = this.uiState.viewList.filter(view => view.id != "uploadNewResiduals" && view.id != "uploadCertUsedResiduals");
    } else {
      if (this.uiState.viewList.findIndex((view) => view.id === "uploadNewResiduals") === -1) {
        this.uiState.viewList.push({id: "uploadNewResiduals", title: "Upload New Residuals"});
      }
      if (this.uiState.viewList.findIndex((view) => view.id === "uploadCertUsedResiduals") === -1) {
        this.uiState.viewList.push({id: "uploadCertUsedResiduals", title: "Upload Certified Residuals"});
      }
      const index = this.uiState.viewList.findIndex((view) => view.id === "residualImporter");
      if (index !== -1) {
        this.uiState.viewList.splice(index, 1);
      }
    }
  }

}
